import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Box, Button, Grid, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";

import whatsappGif from "../common/gif/whatsapp.gif";
import television from "../common/svgs/television.svg";
import AboutUs from "./AboutUs";
import ContactUs from "./ContactUs";
import DigitalSignage from "./DigitalSignage";
import Features from "./Features";
import LandingPageFooter from "./LandingPageFooter";
import LandingPageHeader from "./LandingPageHeader";
import LotteryDisplay from "./LotteryDisplay";
import "./lotterySystem.css";
import MoreFeatures from "./MoreFeatures";
import Pricing from "./Pricing";

const LotterySystem: React.FC = () => {
  const { t } = useTranslation("translation");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Below 600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600px - 1024px

  return (
    <Paper elevation={3} className='landing-paper' id='landing-intro'>
      <LandingPageHeader />

      {/* WhatsApp Floating Icon */}
      <div className='bottom-right-icon'>
        <a href='https://wa.me/18333141825' title='Contact us'>
          <img src={whatsappGif} alt='Whatsapp icon' />
        </a>
      </div>

      {/* Hero Section */}
      <Box className='landing-box'>
        <Grid container spacing={4} alignItems='center'>
          {/* Left Side: Text */}
          <Grid item xs={12} md={6} textAlign='center'>
            <Typography
              className='landing-title landing-headers-orange-color'
              sx={{
                fontSize: isMobile ? "1.8rem" : isTablet ? "2.2rem" : "3rem",
                wordBreak: "break-word",
                textAlign: "left",
                px: 2,
                lineHeight: "1.2",
              }}
            >
              {t("letsGoDigital")}
            </Typography>
            <Typography
              className='landing-description'
              sx={{
                fontSize: isMobile ? "1rem" : "1.2rem",
                mt: 1,
                textAlign: "left",
                px: 2,
              }}
            >
              {t("letsGoDigitalDescription")}
            </Typography>
            <Button
              onClick={() => navigate("/login")}
              className='landing-button get-started-button'
              variant='contained'
              sx={{
                mt: 2,
                fontSize: isMobile ? "0.9rem" : "1rem",
                padding: isMobile ? "10px 18px" : "12px 26px",
                display: "block",
                mx: "auto",
                justifySelf: isMobile ? "center" : "flex-start",
              }}
            >
              {t("getStarted")}
            </Button>
          </Grid>

          {/* Right Side: YouTube Video */}
          <Grid item xs={12} md={6} textAlign='center'>
            <Box
              sx={{
                width: isMobile ? "100%" : "560px",
                maxWidth: "100%",
                aspectRatio: "16/9",
                borderRadius: "10px",
                overflow: "hidden",
                position: "relative",
                zIndex: 1,
              }}
            >
              <iframe
                width='100%'
                height='100%'
                src='https://www.youtube.com/embed/-rQgYNy6OKQ?si=XKATbeL_FcgQZnsJ'
                title='YouTube video player'
                frameBorder='0'
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                referrerPolicy='strict-origin-when-cross-origin'
                allowFullScreen
                style={{ borderRadius: "10px", position: "relative", zIndex: 2 }}
              ></iframe>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Television Image */}
      <Box className='landing-box' sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <img
          src={television}
          alt='television'
          style={{
            width: isMobile ? "90%" : isTablet ? "75%" : "60%",
            maxWidth: "500px",
            height: "auto",
          }}
        />
      </Box>

      {/* Feature Sections */}
      <Box className='landing-box'>
        <LotteryDisplay />
      </Box>
      <Box className='landing-box'>
        <Features />
      </Box>
      <Box className='landing-box'>
        <MoreFeatures />
      </Box>
      <Box className='landing-box'>
        <DigitalSignage />
      </Box>
      <Box className='landing-box'>
        <Pricing />
      </Box>
      <Box className='landing-about-us-body'>
        <AboutUs />
      </Box>
      <Box
        className={`landing-box ${
          isMobile || isTablet
            ? "landing-contact-us-margin-mobile-and-tablet"
            : "landing-contact-us-margin"
        }`}
      >
        <ContactUs />
      </Box>

      {/* Footer Section */}
      <Box sx={{ width: "100%", mt: 4 }}>
        <Box className='landing-footer'>
          <LandingPageFooter />
        </Box>
        <Grid className='bg-[#074470] text-white p-4 mt-auto' textAlign='center'>
          <p>Copyright &copy; 2024-2030 Digi Tech Express | All Rights Reserved</p>
        </Grid>
      </Box>
    </Paper>
  );
};

export default LotterySystem;
